import React, { useState, useRef, useEffect } from 'react';
import { X, RotateCw, ZoomIn, ZoomOut, Sparkles, Download } from 'lucide-react';
import { toast } from 'sonner';

export const ImageEditor = ({ imageUrl, isOpen, onClose, onSave }) => {
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [zoom, setZoom] = useState(1);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (isOpen && imageUrl) {
      loadImage();
    }
  }, [isOpen, imageUrl]);

  const loadImage = () => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    img.onload = () => {
      imageRef.current = img;
      renderCanvas();
    };
  };

  const renderCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imageRef.current) return;

    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    // Set canvas size
    canvas.width = img.width;
    canvas.height = img.height;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context
    ctx.save();

    // Apply transformations
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    // Apply filters
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;

    // Draw image
    ctx.drawImage(img, -img.width / 2, -img.height / 2);

    // Restore context
    ctx.restore();
  };

  useEffect(() => {
    if (imageRef.current) {
      renderCanvas();
    }
  }, [rotation, brightness, contrast, zoom]);

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleReset = () => {
    setRotation(0);
    setBrightness(100);
    setContrast(100);
    setZoom(1);
    toast.success('Zurückgesetzt');
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      onSave(blob);
      onClose();
      toast.success('✅ Bild gespeichert');
    }, 'image/jpeg', 0.95);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-2xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: '#E1E4E8' }}>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900">Bild bearbeiten</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-editor-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 overflow-auto bg-gray-100 p-4 flex items-center justify-center">
          <canvas
            ref={canvasRef}
            className="max-w-full max-h-full shadow-lg"
            style={{ maxHeight: 'calc(90vh - 200px)' }}
          />
        </div>

        {/* Controls */}
        <div className="p-4 border-t bg-gray-50" style={{ borderColor: '#E1E4E8' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Rotation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Drehen: {rotation}°
              </label>
              <button
                onClick={handleRotate}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                data-testid="rotate-button"
              >
                <RotateCw className="w-4 h-4" />
                90° drehen
              </button>
            </div>

            {/* Brightness */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Helligkeit: {brightness}%
              </label>
              <input
                type="range"
                min="50"
                max="150"
                value={brightness}
                onChange={(e) => setBrightness(Number(e.target.value))}
                className="w-full"
                data-testid="brightness-slider"
              />
            </div>

            {/* Contrast */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kontrast: {contrast}%
              </label>
              <input
                type="range"
                min="50"
                max="150"
                value={contrast}
                onChange={(e) => setContrast(Number(e.target.value))}
                className="w-full"
                data-testid="contrast-slider"
              />
            </div>

            {/* Zoom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zoom: {Math.round(zoom * 100)}%
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setZoom((prev) => Math.max(0.5, prev - 0.1))}
                  className="flex-1 flex items-center justify-center px-2 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  data-testid="zoom-out-button"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setZoom((prev) => Math.min(2, prev + 0.1))}
                  className="flex-1 flex items-center justify-center px-2 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  data-testid="zoom-in-button"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              data-testid="reset-button"
            >
              Zurücksetzen
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              data-testid="cancel-button"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2"
              style={{ backgroundColor: '#3B82F6' }}
              data-testid="save-button"
            >
              <Download className="w-4 h-4" />
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
